/**
 * Mock Batch Data Service
 * Provides sample batch data for testing and development
 * This is used when the backend API is not available
 *
 * @author SAUBHAGYA Development Team
 * @since US-BATCH-002
 */

import type { BatchResponse } from '@/types/inventory';

/**
 * Sample batch data for testing the batch details dialog
 */
export const MOCK_BATCHES: BatchResponse[] = [
  {
    id: 1,
    batchNumber: 'BG-2024-001',
    productType: 'COMPRESSED',
    qualityGrade: 'A',
    purityPercentage: 98.5,
    productionDate: '2024-09-15',
    expiryDate: '2025-01-15',
    initialQuantity: 5000,
    currentQuantity: 5000,
    allocatedQuantity: 1500,
    availableQuantity: 3500,
    quantityUnit: 'CUBIC_METERS',
    batchStatus: 'AVAILABLE',
    storageLocation: 'plant_001 - tank_A1',
    pesoCompliant: true,
    pesoCertificate: {
      number: 'PESO-2024-001',
      inspector: 'Dr. Rajesh Kumar',
      issueDate: '2024-09-15',
      expiryDate: '2025-01-15',
      fileUrl: '#'
    },
    allocations: [
      {
        orderId: 'ORD-001',
        orderNumber: 'ORD-2024-001',
        customerName: 'Rajesh Industries',
        quantity: 500,
        orderDate: '2024-10-01',
        orderStatus: 'CONFIRMED'
      },
      {
        orderId: 'ORD-002',
        orderNumber: 'ORD-2024-002',
        customerName: 'Green Energy Ltd',
        quantity: 1000,
        orderDate: '2024-10-05',
        orderStatus: 'SHIPPED'
      }
    ],
    auditLogs: [
      {
        id: '1',
        timestamp: '2024-09-15T10:30:00Z',
        action: 'CREATED',
        performedBy: 'Biogas Operator',
        details: 'Batch created with initial quantity of 5000 cubic meters',
        status: 'success'
      },
      {
        id: '2',
        timestamp: '2024-09-16T14:20:00Z',
        action: 'QUALITY_UPDATED',
        performedBy: 'Quality Inspector',
        details: 'Quality grade set to A with 98.5% purity',
        status: 'success'
      },
      {
        id: '3',
        timestamp: '2024-09-17T09:15:00Z',
        action: 'PESO_CERTIFIED',
        performedBy: 'Compliance Officer',
        details: 'PESO certification completed with certificate number PESO-2024-001',
        status: 'success'
      }
    ]
  },
  {
    id: 2,
    batchNumber: 'CG-2024-005',
    productType: 'BIO_CNG',
    qualityGrade: 'B',
    purityPercentage: 96.2,
    productionDate: '2024-09-20',
    expiryDate: '2025-02-01',
    initialQuantity: 2000,
    currentQuantity: 2000,
    allocatedQuantity: 200,
    availableQuantity: 1800,
    quantityUnit: 'KG',
    batchStatus: 'AVAILABLE',
    storageLocation: 'plant_002 - tank_B2',
    pesoCompliant: true,
    pesoCertificate: {
      number: 'PESO-2024-002',
      inspector: 'Dr. Priya Sharma',
      issueDate: '2024-09-20',
      expiryDate: '2025-02-01',
      fileUrl: '#'
    },
    allocations: [
      {
        orderId: 'ORD-003',
        orderNumber: 'ORD-2024-003',
        customerName: 'Eco Power Solutions',
        quantity: 200,
        orderDate: '2024-10-10',
        orderStatus: 'PENDING'
      }
    ],
    auditLogs: [
      {
        id: '4',
        timestamp: '2024-09-20T11:00:00Z',
        action: 'CREATED',
        performedBy: 'Biogas Operator',
        details: 'Compressed gas batch created with 2000 kg',
        status: 'success'
      },
      {
        id: '5',
        timestamp: '2024-09-21T10:30:00Z',
        action: 'QUALITY_UPDATED',
        performedBy: 'Quality Inspector',
        details: 'Quality grade set to B with 96.2% purity',
        status: 'success'
      }
    ]
  },
  {
    id: 3,
    batchNumber: 'BG-2024-003',
    productType: 'LIQUIFIED',
    qualityGrade: 'C',
    purityPercentage: 92.1,
    productionDate: '2024-08-15',
    expiryDate: '2024-11-15',
    initialQuantity: 3000,
    currentQuantity: 3000,
    allocatedQuantity: 2800,
    availableQuantity: 200,
    quantityUnit: 'CUBIC_METERS',
    batchStatus: 'ALLOCATED',
    storageLocation: 'plant_001 - tank_A2',
    pesoCompliant: false,
    pesoCertificate: undefined,
    allocations: [
      {
        orderId: 'ORD-004',
        orderNumber: 'ORD-2024-004',
        customerName: 'Industrial Gas Corp',
        quantity: 2800,
        orderDate: '2024-09-01',
        orderStatus: 'DELIVERED'
      }
    ],
    auditLogs: [
      {
        id: '6',
        timestamp: '2024-08-15T09:00:00Z',
        action: 'CREATED',
        performedBy: 'Biogas Operator',
        details: 'Batch created with initial quantity of 3000 cubic meters',
        status: 'success'
      }
    ]
  },
  {
    id: 4,
    batchNumber: 'BG-2024-002',
    productType: 'LIQUIFIED',
    qualityGrade: 'A',
    purityPercentage: 99.1,
    productionDate: '2024-07-10',
    expiryDate: '2024-10-10',
    initialQuantity: 4000,
    currentQuantity: 100,
    allocatedQuantity: 3900,
    availableQuantity: 100,
    quantityUnit: 'CUBIC_METERS',
    batchStatus: 'EXPIRED',
    storageLocation: 'plant_003 - tank_C1',
    pesoCompliant: true,
    pesoCertificate: {
      number: 'PESO-2024-003',
      inspector: 'Dr. Amit Patel',
      issueDate: '2024-07-10',
      expiryDate: '2024-10-10',
      fileUrl: '#'
    },
    allocations: [],
    auditLogs: [
      {
        id: '7',
        timestamp: '2024-07-10T08:00:00Z',
        action: 'CREATED',
        performedBy: 'Biogas Operator',
        details: 'Batch created with initial quantity of 4000 cubic meters',
        status: 'success'
      }
    ]
  }
];

/**
 * Get all mock batches
 */
export function getMockBatches(): BatchResponse[] {
  return MOCK_BATCHES;
}

/**
 * Get mock batch by ID
 */
export function getMockBatchById(id: number): BatchResponse | undefined {
  return MOCK_BATCHES.find(batch => batch.id === id);
}

/**
 * Get mock batch by batch number
 */
export function getMockBatchByNumber(batchNumber: string): BatchResponse | undefined {
  return MOCK_BATCHES.find(batch => batch.batchNumber === batchNumber);
}

/**
 * Filter mock batches by status
 */
export function getMockBatchesByStatus(status: string): BatchResponse[] {
  return MOCK_BATCHES.filter(batch => batch.batchStatus === status);
}

export default {
  getMockBatches,
  getMockBatchById,
  getMockBatchByNumber,
  getMockBatchesByStatus
};
